import { COST_ARTICLE_STATUSES, type CostArticlesResponse, type CostArticleView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { costArticlesQuery } from '../lib/articles';
import { dateTime } from '../lib/format';
import { Button } from '../ui/button';
import { Badge, ErrorBox, Spinner, type Tone, toast } from '../ui/feedback';
import { Field, Input, Textarea } from '../ui/form';
import { Page, PageHeader, Panel } from '../ui/layout';

const TONE: Record<CostArticleView['status'], Tone> = {
  requested: 'info',
  checked: 'warning',
  approved: 'success',
  rejected: 'neutral',
};

function Decision({ a, step }: { a: CostArticleView; step: 'check' | 'decide' }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const m = useMutation({
    mutationFn: (approve: boolean) =>
      api<CostArticlesResponse>(`/cost-articles/${a.id}/${step}`, {
        body: { approve, comment: comment.trim() || undefined },
      }),
    onSuccess: (r, approve) => {
      qc.setQueryData(costArticlesQuery.queryKey, r);
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast(
        approve ? (step === 'check' ? 'Проверено, передано Директору' : 'Статья утверждена') : 'Отклонено',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={m.isPending && m.variables}
          onClick={() => m.mutate(true)}
        >
          {step === 'check' ? 'Проверено, на решение Директору' : 'Утвердить статью'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setRejecting((v) => !v)}>
          Отклонить
        </Button>
      </div>
      {rejecting ? (
        <div className="flex max-w-xl flex-col gap-2">
          <Textarea
            rows={2}
            placeholder="Причина отказа"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div>
            <Button
              size="sm"
              variant="danger"
              disabled={!comment.trim()}
              loading={m.isPending && m.variables === false}
              onClick={() => m.mutate(false)}
            >
              Отклонить с причиной
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Статьи расходов (БМ-14, БМ-15): двенадцать утверждённых и запросы новых статей */
export function CostArticlesPage() {
  const qc = useQueryClient();
  const q = useQuery(costArticlesQuery);
  const [f, setF] = useState({ name: '', reason: '' });
  const create = useMutation({
    mutationFn: () => api<CostArticlesResponse>('/cost-articles', { body: f }),
    onSuccess: (r) => {
      qc.setQueryData(costArticlesQuery.queryKey, r);
      setF({ name: '', reason: '' });
      toast('Запрос отправлен финансовому менеджеру');
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const open = d.requests.filter((r) => r.status === 'requested' || r.status === 'checked');
  const done = d.requests.filter((r) => r.status === 'approved' || r.status === 'rejected');
  return (
    <Page>
      <PageHeader
        title="Статьи расходов"
        subtitle="Двенадцать утверждённых статей финансового плана; новая статья — только запросом: финансовый менеджер проверяет, Директор утверждает (БМ-15)"
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-6">
          <Panel title="Запросы на рассмотрении" meta={open.length ? String(open.length) : undefined}>
            {open.length ? (
              <ul className="divide-y divide-line">
                {open.map((a) => (
                  <li key={a.id} className="py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-[15px] font-medium text-ink">{a.name}</p>
                      <Badge tone={TONE[a.status]}>{COST_ARTICLE_STATUSES[a.status]}</Badge>
                    </div>
                    <p className="mt-1 text-[14px] text-ink">{a.reason}</p>
                    <p className="mt-1 text-[12px] text-ink-2">
                      Запросил {a.requestedBy ?? '—'}, {dateTime(a.requestedAt)}
                      {a.checkedAt ? ` · проверил ${a.checkedBy ?? '—'}, ${dateTime(a.checkedAt)}` : ''}
                    </p>
                    {a.status === 'requested' && d.can.check ? <Decision a={a} step="check" /> : null}
                    {a.status === 'checked' && d.can.decide ? <Decision a={a} step="decide" /> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Открытых запросов нет.</p>
            )}
          </Panel>
          {done.length ? (
            <Panel title="Решения">
              <ul className="divide-y divide-line">
                {done.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 py-2.5">
                    <div>
                      <p className="text-[14px] text-ink">{a.name}</p>
                      <p className="text-[12px] text-ink-2">
                        {a.status === 'approved'
                          ? `утвердил ${a.decidedBy ?? '—'}, ${dateTime(a.decidedAt)}`
                          : `отклонено: ${a.rejectReason ?? '—'}`}
                      </p>
                    </div>
                    <Badge tone={TONE[a.status]}>{COST_ARTICLE_STATUSES[a.status]}</Badge>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
        <div className="flex flex-col gap-6">
          {d.can.request ? (
            <Panel title="Запросить новую статью">
              <div className="flex flex-col gap-3">
                <Field label="Название статьи">
                  <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                </Field>
                <Field label="Зачем она нужна">
                  <Textarea
                    rows={3}
                    value={f.reason}
                    placeholder="Какие расходы в неё попадут и почему они не подходят ни под одну из двенадцати статей"
                    onChange={(e) => setF({ ...f, reason: e.target.value })}
                  />
                </Field>
                <div>
                  <Button
                    variant="primary"
                    disabled={f.name.trim().length < 2 || f.reason.trim().length < 5}
                    loading={create.isPending}
                    onClick={() => create.mutate()}
                  >
                    Отправить запрос
                  </Button>
                </div>
                <ErrorBox error={create.error} />
              </div>
            </Panel>
          ) : null}
          <Panel title="Действующие статьи" meta={String(d.active.length)}>
            <ol className="flex list-decimal flex-col gap-1 pl-5 text-[14px] text-ink">
              {d.active.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ol>
          </Panel>
        </div>
      </div>
    </Page>
  );
}
